/**
 * `/v1/ops/account-balance-alerts/*` transport (#1892).
 *
 * Raw calls only — React Query wiring lives in `useAccountBalanceAlerts.ts`,
 * matching the `emailNotices.ts` / `useEmailNotices.ts` split.
 *
 * Note what is NOT here: no client-side copy of the switch's default, the
 * suppression semantics, or the preview criteria. All three are the platform
 * route's, and a second statement of them here would be the one that drifts.
 * In particular the console never assumes "no settings row means on" — the
 * absence of the row is read as `false` server-side, and reading it any other
 * way in the browser would re-create the failure the shipped-closed default
 * exists to prevent.
 */
import type {
  AccountBalanceAlertHistoryResponse,
  AccountBalanceAlertPreviewResponse,
  AccountBalanceAlertSettingsResponse,
  AccountBalanceAlertSuppressionsResponse,
  CreateAccountBalanceAlertSuppressionRequest,
  CreateAccountBalanceAlertSuppressionResponse,
  DeleteAccountBalanceAlertSuppressionResponse,
  UpdateAccountBalanceAlertSettingsRequest,
} from '@nadoutong/contracts';
import { opsApiRequest } from './client.ts';

const BASE = '/v1/ops/account-balance-alerts';

/**
 * How many history rows one read asks for.
 *
 * Sent EXPLICITLY rather than left to the route's own default, even though the
 * two currently agree. The console has to tell an operator whether what they
 * are looking at is the whole record, and the only signal available for that —
 * the response carries no `has_more` — is "did I get back as many rows as I
 * asked for". That comparison is only sound if the console knows the number,
 * so it states it instead of inferring it. A server-side default that moved
 * would otherwise silently turn a truthful "this is everything" into a lie.
 *
 * The route caps at 500; this stays well under it.
 */
export const ACCOUNT_BALANCE_ALERT_HISTORY_LIMIT = 100;

function suppressionPath(accountId: string): string {
  return `${BASE}/suppressions/${encodeURIComponent(accountId)}`;
}

export async function getAccountBalanceAlertSettings(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<AccountBalanceAlertSettingsResponse> {
  return opsApiRequest<AccountBalanceAlertSettingsResponse>(
    `${BASE}/settings`,
    { signal },
    fetchImpl,
  );
}

export async function updateAccountBalanceAlertSettings(
  body: UpdateAccountBalanceAlertSettingsRequest,
  fetchImpl: typeof fetch,
): Promise<AccountBalanceAlertSettingsResponse> {
  return opsApiRequest<AccountBalanceAlertSettingsResponse>(
    `${BASE}/settings`,
    { method: 'PATCH', body },
    fetchImpl,
  );
}

export async function listAccountBalanceAlertSuppressions(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<AccountBalanceAlertSuppressionsResponse> {
  return opsApiRequest<AccountBalanceAlertSuppressionsResponse>(
    `${BASE}/suppressions`,
    { signal },
    fetchImpl,
  );
}

/**
 * PUT, because re-muting an already-muted account is the same request twice
 * and must not be an error. The response reports whether an armed dedup row
 * was destroyed by the call — the visible half of the decided semantics, which
 * the console surfaces rather than leaving to be discovered later.
 */
export async function createAccountBalanceAlertSuppression(
  accountId: string,
  body: CreateAccountBalanceAlertSuppressionRequest,
  fetchImpl: typeof fetch,
): Promise<CreateAccountBalanceAlertSuppressionResponse> {
  return opsApiRequest<CreateAccountBalanceAlertSuppressionResponse>(
    suppressionPath(accountId),
    { method: 'PUT', body },
    fetchImpl,
  );
}

export async function deleteAccountBalanceAlertSuppression(
  accountId: string,
  fetchImpl: typeof fetch,
): Promise<DeleteAccountBalanceAlertSuppressionResponse> {
  return opsApiRequest<DeleteAccountBalanceAlertSuppressionResponse>(
    suppressionPath(accountId),
    { method: 'DELETE' },
    fetchImpl,
  );
}

export async function listAccountBalanceAlertHistory(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<AccountBalanceAlertHistoryResponse> {
  return opsApiRequest<AccountBalanceAlertHistoryResponse>(
    `${BASE}/history?limit=${ACCOUNT_BALANCE_ALERT_HISTORY_LIMIT}`,
    { signal },
    fetchImpl,
  );
}

/**
 * Computes the next pass's target population and writes nothing.
 *
 * A GET, unlike #1877's recipient preview: this takes no composed body and is
 * the answer to a question an operator asks repeatedly while deciding whether
 * to mute somebody.
 */
export async function getAccountBalanceAlertPreview(
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<AccountBalanceAlertPreviewResponse> {
  return opsApiRequest<AccountBalanceAlertPreviewResponse>(
    `${BASE}/preview`,
    { signal },
    fetchImpl,
  );
}
